import { AccountService } from '@inter-ikea-kompis/core/services'
import type { IDexfSettings } from '@inter-ikea-kompis/core/types'
import { GlobalCacheStore } from '@inter-ikea-kompis/core/utilities'

import { KOMPIS_SERVICE_BASE_SETTINGS } from '@/integrations/kompis/constants'

import type { InsightsApi } from '@insights/insights-data-provider'

const ACCOUNT_SERVICE_CACHE_NAME = 'accountService'
const ACCOUNT_SERVICE_USER_CACHE_KEY = 'user'

const accountServicesBySettings = new Map<IDexfSettings, AccountService>()

let authenticationStateSyncAbortController: AbortController | null = null

function clearCachedAccountUser() {
  new GlobalCacheStore().getStore(ACCOUNT_SERVICE_CACHE_NAME).delete(ACCOUNT_SERVICE_USER_CACHE_KEY)
}

function getOrCreateAccountService(dexfSettings: IDexfSettings) {
  const cachedAccountService = accountServicesBySettings.get(dexfSettings)
  if (cachedAccountService) {
    return cachedAccountService
  }

  const accountService = new AccountService({
    ...KOMPIS_SERVICE_BASE_SETTINGS,
    settings: dexfSettings,
  })

  accountServicesBySettings.set(dexfSettings, accountService)
  return accountService
}

export async function getAuthenticationState(
  dexfSettings: IDexfSettings,
  useSilentAuthentication = false,
): Promise<boolean> {
  if (dexfSettings.kompis.serviceSettings.accountDataSource === 'disabled') {
    return false
  }

  clearCachedAccountUser()

  const user = await getOrCreateAccountService(dexfSettings).getUser({
    useSilentAuthentication,
  })

  return user !== null
}

export async function tryGetAuthenticationState(
  dexfSettings: IDexfSettings,
  useSilentAuthentication: boolean,
): Promise<boolean> {
  try {
    return await getAuthenticationState(dexfSettings, useSilentAuthentication)
  } catch (error) {
    console.error('Failed to resolve authentication state for analytics.', error)
    return false
  }
}

export async function syncInsightsAuthenticationState(
  insightsApi: Pick<InsightsApi, 'setLoggedIn'>,
  dexfSettings: IDexfSettings,
  useSilentAuthentication = false,
): Promise<void> {
  await insightsApi.setLoggedIn(await getAuthenticationState(dexfSettings, useSilentAuthentication))
}

export function registerAuthenticationStateSync(
  insightsApi: InsightsApi,
  dexfSettings: IDexfSettings,
  useSilentAuthentication = false,
) {
  const syncAuthenticationState = () => {
    void syncInsightsAuthenticationState(insightsApi, dexfSettings, useSilentAuthentication).catch((error) => {
      console.error('Failed to sync authentication state for analytics.', error)
    })
  }

  const syncWhenVisible = () => {
    if (document.visibilityState === 'visible') {
      syncAuthenticationState()
    }
  }

  authenticationStateSyncAbortController?.abort()
  authenticationStateSyncAbortController = new AbortController()

  const { signal } = authenticationStateSyncAbortController

  window.addEventListener('focus', syncAuthenticationState, { signal })
  window.addEventListener('pageshow', syncAuthenticationState, { signal })
  document.addEventListener('visibilitychange', syncWhenVisible, { signal })
}
